import { Guid } from 'guid-typescript';
import moment from 'moment';

import { getLocale } from '@/helpers/locale-helper';
import { ExceptionType, AppLogLevel, Errors } from '@/models/enums';
import { parseOracleErrMsg, parsePostgresErrMsg } from '@/helpers/errformat-helper';

export interface LoggerSetting {
  consoleLogLevel: string;
  storageLogLevel: string;
}

export class LogStorage {
  messages: { [email: string]: Array<Message> };
  settings: { [email: string]: LoggerSetting };
  version: string;
  constructor(v?: string) {
    this.messages = {};
    this.settings = {};
    this.version = v || '1.0.0.0';
  }
}

export class Message {
  id: string;
  dateReceived: string;
  dateReported: string = '';
  error: AppError | undefined;
  text: string;
  data: any;
  level: string;
  constructor(level: AppLogLevel, incMessage?: string, error?: AppError, data?: any) {
    this.id = Guid.raw();
    this.level = AppLogLevel[level];
    this.dateReceived = moment().format('DD/MM/YYYY HH:mm:ss');
    this.error = error;
    this.text = incMessage || this.parseMessage(error);
    this.data = data;
  }

  get isError(): boolean {
    return this.level === AppLogLevel[AppLogLevel.Error];
  }

  parseMessage(error?: AppError): string {
    let msg = '';
    if (this.isError) {
      msg = error ? (error.parsedMessage || getLocale('errors.unknown').toString()) : '';
    }
    return msg;
  }
}

export class AppError {
  code: string;
  details: AppErrorDetail[];
  errorId: string;
  message: string;
  requestId: string;
  stackTrace: string;
  status: number;
  statusText: string;
  target: string;
  constructor({ code, details, errorId, message, requestId, stackTrace, stack, status, statusText, target }) {
    this.code = code;
    this.details = details ? details.map(d => new AppErrorDetail(d)) : [];
    this.errorId = errorId;
    this.message = (message || (status === 401 ? getLocale('errors.unauthorized') : ''));
    this.requestId = requestId;
    this.stackTrace = stackTrace || stack;
    this.status = status;
    this.statusText = statusText;
    this.target = target;
  }
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isOracleException(): boolean {
    return (!!this.code && this.code === ExceptionType.OracleException) ||
      (!!this.message && /ORA-20101/ig.test(this.message));
  }

  get isPostgresException(): boolean {
    return (!!this.code && this.code === ExceptionType.PostgresException) ||
      (!!this.message && /2010\d:/ig.test(this.message) && !/ora/ig.test(this.message));
  }

  get parsedMessage(): string {
    let msg = '';
    if (this.isOracleException) {
      msg = parseOracleErrMsg(this.message);
    } else if (this.isPostgresException) {
      msg = parsePostgresErrMsg(this.message);
    } else {
      msg = this.message;
    }
    if (this.details && this.details.length) {
      msg = `${msg}<br/>${this.details.map(d => `&nbsp;-&nbsp;${d.description || d.name}`).join('<br/>')}`;
    }
    return msg;
  }

  get isKeyEdrpouMismatchError(): boolean {
    return this._isErrorDetailsContainName(Errors.KeyEdrpouMismatch);
  }

  get isEmailNotConfirmedError(): boolean {
    return this._isErrorDetailsContainName(Errors.EmailNotConfirmed);
  }

  get isInternalNestedSignatureChangedDuringSigningError(): boolean {
    return this._isErrorDetailsContainName(Errors.InternalNestedSignatureChangedDuringSigning);
  }

  private _isErrorDetailsContainName(name: string) {
    return this.details && this.details.length > 0 && this.details.some(d => d.name === name);
  }
}

export class AppErrorDetail {
  description: string;
  key: string;
  name: string;
  constructor({ description, key, name }) {
    this.description = description;
    this.key = key;
    this.name = name;
  }
}
